'use client';

import { type MouseEvent, useState } from 'react';

import ImageOptimizer from '@/components/image-optimizer';
import ImageViewer from '@/components/image-viewer';
import { AppInterface } from '@/enums/app-interface.enum';
import { sendToApp } from '@/lib/app-interface';
import { BlogReviewItem as BlogReviewItemType } from '@/services/types/review.type';
import { useMemberStore } from '@/stores/member.store';

interface BlogReviewItemProps {
  review: BlogReviewItemType;
}

export default function BlogReviewItem({ review }: BlogReviewItemProps) {
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const isAppConnected = useMemberStore((s) => s.isAppConnected);

  const handleImageClick = (index: number) => {
    console.log(`handleImageClick[${index}]`);
    setSelectedImageIndex(index);
    setImageViewerOpen(true);
  };

  // 외부 블로그 글로 이동. 웹은 앵커 기본 동작(새 탭), 앱 연결 시에만
  // 가로채 sendToApp으로 위임한다.
  const handleOpenReview = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!isAppConnected) return;
    e.preventDefault();
    sendToApp(AppInterface.REQ_OPEN_URL, {
      type: 'internal',
      url: review.link,
    });
  };

  return (
    <div className="flex flex-col py-5 cursor-pointer">
      {review.images.length > 0 && review.images[0].savePath && (
        <div className="flex gap-0.5 overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {review.images.map((image, index) => (
            <div
              key={`${review.title}-${index}`}
              className={`relative flex-shrink-0 w-[140px] h-[94px] border-[4px] border-white cursor-pointer
                ${index === 0 ? 'ml-5' : ''} ${index === review.images.length - 1 ? 'mr-5' : ''}
              `}
              onClick={() => handleImageClick(index)}
            >
              <ImageOptimizer
                src={image.thumbSavePath ?? image.savePath}
                alt={review.title}
                fill
                className="object-cover rounded"
              />
            </div>
          ))}
        </div>
      )}
      <ImageViewer
        key={`image-viewer-${selectedImageIndex}`}
        images={review.images}
        initialIndex={selectedImageIndex}
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
      />
      <a
        href={review.link || undefined}
        target="_blank"
        rel="noopener noreferrer nofollow"
        onClick={handleOpenReview}
      >
        <div className="px-5">
          <div className="mb-2">
            <span
              className="typo-15 font-bold text-222222 line-clamp-1 pointer-events-none"
              dangerouslySetInnerHTML={{ __html: review.title }}
            />
          </div>
          <div className="mb-5">
            <span
              className="typo-14 font-medium text-333333 line-clamp-1 pointer-events-none"
              dangerouslySetInnerHTML={{ __html: review.content }}
            />
          </div>

          <span className="typo-12 font-medium text-[#908E9E]">
            {review.postdate.length === 8
              ? `${review.postdate.slice(0, 4)}.${review.postdate.slice(4, 6)}.${review.postdate.slice(6, 8)}`
              : review.postdate}
          </span>
        </div>
      </a>
    </div>
  );
}
